
import { CheckCircle, Description, Info, Visibility } from "@mui/icons-material";
import GetAppIcon from "@mui/icons-material/GetApp";
import { Box, IconButton, Tooltip } from "@mui/material";
import { MaterialReactTable, MRT_ToggleDensePaddingButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton, useMaterialReactTable } from "material-react-table";
import { useMemo, useRef, useState } from "react";
import { getFormatoFechaExportacion } from "../../../../utils/datetimeUtils";
import { downloadCsv } from "../../../../utils/filesUtils";
import snackbarUtils from "../../../../utils/snackbarUtils";
import ConfirmDialog from "../../../commons/dialog/confirmDialog";
import { dateAccesor } from "../../../commons/table/columns/accesors";
import { localization, tableConfig, } from "../../../commons/table/config/tableConfig";
import TitleContainer from "../../../commons/table/utilsTable/titleWithFetchData";

const NovedadesTable = ({
  handleOpenEdit,
  setSelectedRows,
  namesArts,
  handleOpenRespuestas,
  handleOpenXmlPreview,
  handleSubmitSelected,
  data,
  loading,
  fetchData,
  noRefresh,
}) => {
  const classes = {};
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const columns = useMemo(() => [
    {
      header: "Art",
      accessorKey: "art",
      Cell: ({ row }) => row.original.nombreArt.toUpperCase(),
      enableColumnFilter: false,
    },
    { header: "Siniestro", accessorKey: "historiaClinica" },
    { header: "Documento", accessorKey: "documento", },
    { header: "Trabajador", accessorKey: "trabajador", },
    { header: "Empleador", accessorKey: "empleador" },
    { header: "Prestador", accessorKey: "prestador" },
    { header: "CUIT Prestador", accessorKey: "cuitPrestador" },
    {
      header: "Fecha Formulario",
      accessorKey: "fechaFormulario",
      accessorFn: dateAccesor("fechaFormulario"),
      filterFn: "includesString"
    },
    { header: "Formulario", accessorKey: "formulario" },
    {
      header: "Fecha Procesado",
      accessorKey: "fechaProcesado",
      accessorFn: dateAccesor("fechaProcesado"),
      filterFn: "includesString"
    },

  ], []);

  const [selectedRows, setSelectedRowsState] = useState([]);
  const tableRef = useRef();

  const formatTableDataForExport = (table) => {
    return table.map((fila) => [
      // Limpiar los espacios innecesarios y formatear los datos
      fila.nombreArt ? `"${fila.nombreArt.trim()}"` : "",
      fila.historiaClinica ? `"${fila.historiaClinica.trim()}"` : "",
      fila.documento ? `"${fila.documento}"` : "",
      fila.trabajador ? `"${fila.trabajador.trim()}"` : "",
      fila.empleador ? `"${fila.empleador.trim()}"` : "",
      fila.prestador ? `"${fila.prestador.trim()}"` : "",
      getFormatoFechaExportacion(fila.fechaFormulario, "P") || "", // Aseguramos que la fecha sea válida
      fila.formulario ? `"${fila.formulario.trim()}"` : "",
      getFormatoFechaExportacion(fila.fechaProcesado, "P") || "", // Aseguramos que la fecha sea válida
    ]);
  };

  const handleExportToCsv = () => {
    setIsDownloadingCsv(true);

    // Encabezado correcto según tu ejemplo
    const encabezado = [
      "Art",
      "Siniestro",
      "Documento",
      "Trabajador",
      "Empleador",
      "Prestador",
      "Fecha Formulario",
      "Formulario",
      "Fecha Procesado",
    ];

    // Formateamos los datos de la tabla
    const formattedDataAsCsv = [encabezado, ...formatTableDataForExport(data)];

    // Generamos el contenido CSV
    let csvContent = "sep=,\n"; // Definimos el separador
    csvContent += `${formattedDataAsCsv.map((e) => e.join(",")).join("\n")}`; // Unimos las filas por saltos de línea

    // Llamamos a la función para descargar el archivo CSV
    downloadCsv(csvContent, "Novedades");
    snackbarUtils.success("Las novedades han sido descargados con éxito.");
    setIsDownloadingCsv(false);
  };

  const handleCloseConfirmDialog = () => { setOpenConfirmDialog(false); }


  const table = useMaterialReactTable({
    ...tableConfig,
    positionToolbarAlertBanner: "top",
    onRowSelectionChange: setRowSelection,
    enableSelectAll: true,
    enableRowSelection: true,
    columns: columns,
    enableRowActions: true,
    positionActionsColumn: "last",
    displayColumnDefOptions: {
      ...tableConfig.displayColumnDefOptions,
      'mrt-row-actions': {
        size: 130, // Tamaño suficiente para 3 botones
        grow: true,
        muiTableHeadCellProps: {
          sx: {
            ...tableConfig.muiTableHeadCellProps.sx,
            justifyContent: 'flex-start', // <--- Esto empuja el título hacia arriba
          }
        }
      },
    },
    renderRowActions: ({ row }) => (
      <>
        <Tooltip title={"Ver Respuestas"}>
          <IconButton onClick={() => handleOpenRespuestas(row.original)} size="small" color="inherit">
            <Info className={classes.icon} />
          </IconButton>
        </Tooltip>
        <Tooltip title={"Ver Detalles"}>
          <IconButton onClick={() => handleOpenEdit(row.original)} size="small" color="inherit">
            <Visibility className={classes.icon} />
          </IconButton>
        </Tooltip>
        <Tooltip title={"Visualizar XML"}>
          <IconButton onClick={() => handleOpenXmlPreview(row.original)} size="small" color="inherit">
            <Description className={classes.icon} />
          </IconButton>
        </Tooltip>
      </>
    ),
    renderTopToolbarCustomActions: () => {
      return Object.keys(rowSelection).length > 0 ? undefined : ({ table }) => {
        return (
          <Box m={0.5}>
            <TitleContainer
              title="Novedades"
              noRefresh={noRefresh}
              fetchData={fetchData}
            />
          </Box>
        )
      }
    },
    data: data,
    state: { isLoading: loading || isDownloadingCsv, rowSelection },
    renderToolbarInternalActions: ({ table }) => {
      const selectedRows = table?.getSelectedRowModel()?.rows?.map((row) => row.original);
      if (selectedRows?.length > 0) {
        return (
          <Tooltip title="Reenviar Novedades">
            <IconButton
              sx={{ color: "secondary.main" }}
              onClick={() => { setOpenConfirmDialog(true); }}>
              <CheckCircle />
            </IconButton>
          </Tooltip>
        )
      }
      return (
        <>
          <Tooltip title="Exportar CSV" arrow="top">
            <IconButton
              color="inherit"
              onClick={handleExportToCsv}>
              <GetAppIcon />
            </IconButton>
          </Tooltip>
          <MRT_ToggleFiltersButton table={table} />
          <MRT_ToggleDensePaddingButton table={table} />
          <MRT_ToggleFullScreenButton table={table} />
        </>
      )
    },
    muiTableBodyCellProps: localization.cells,
    onSelectionChange: (rows) => {
      setSelectedRows(rows);
      setSelectedRowsState(rows);
    },
    layoutMode: "grid",

  });
  const selectedRowsData = table.getSelectedRowModel().rows.map((row) => row.original);

  return (
    <div >
      <MaterialReactTable table={table} />
      <ConfirmDialog
        open={openConfirmDialog}
        handleClose={handleCloseConfirmDialog}
        handleConfirm={() => {
          handleSubmitSelected(selectedRowsData);
          setOpenConfirmDialog(false);
          table.setRowSelection({});
        }}
        title={`¿Está seguro que desea reenviar ${selectedRowsData.length} ${selectedRowsData.length === 1 ? 'novedad' : 'novedades'} a la ART?`}
      />
    </div>
  );
};

export default NovedadesTable;